import { useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import type { PartnershipAttributeValue } from '@/admin/types/organisations'

/**
 * PATCH /organisations/{orgUuid}/partnerships/{uuid}/attribute-values/
 *
 * Partial update - only the keys present in the payload are written.
 * Writing the `tier` code_name routes through the canonical sync helper
 * on the backend, so ACL grants reshape atomically.
 *
 * Returns the updated attribute_values list on success.
 */
export function useUpdatePartnershipAttributeValues(
  partnershipUuid: string | undefined,
) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api
        .patch(
          `/organisations/${orgUuid}/partnerships/${partnershipUuid}/attribute-values/`,
          payload,
        )
        .then((r) => (r.data.result ?? r.data) as PartnershipAttributeValue[]),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partnership', orgUuid, partnershipUuid],
      })
      // The product-access / template-access keys don't need invalidation
      // directly here - tier writes re-sync on the backend and the next
      // GET on those keys will pick up the new state.
    },
  })
}
